import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import { submitChallengeSignup } from "@/lib/api";

export default function ChallengeSignup({ compact = false }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please share your first name and email.");
      return;
    }
    setLoading(true);
    try {
      await submitChallengeSignup({ name: name.trim(), email: email.trim() });
      setDone(true);
      toast.success("You're in. Day 1 lands in your inbox shortly.");
      setName("");
      setEmail("");
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Try again in a moment.";
      toast.error(typeof msg === "string" ? msg : "Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="challenge-signup-card"
      className={`relative overflow-hidden rounded-2xl border ${compact ? "p-7" : "p-9 md:p-12"}`}
      style={{
        background: "#FAF6EC",
        borderColor: "rgba(201,168,76,0.5)",
        boxShadow: "0 30px 60px -30px rgba(27,67,50,0.35)",
      }}
    >
      <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full opacity-20"
           style={{ background: "radial-gradient(circle at center, #C9A84C 0%, transparent 65%)" }} />
      <div className="relative">
        <div className="flex items-center gap-2">
          <Sparkles size={16} style={{ color: "#C9A84C" }} />
          <span className="eyebrow">Free 7-Day Challenge</span>
        </div>
        <h3 className={`font-serif ${compact ? "text-3xl" : "text-4xl md:text-5xl"} mt-3 leading-tight`}>
          7 days to stop running on autopilot.
        </h3>
        <p className="mt-4 max-w-xl text-[15px] leading-relaxed" style={{ color: "#2A2621" }}>
          A free challenge for men who don&rsquo;t have time for this &mdash; which is exactly why
          you need it. Seven days. Seven small, honest practices. No fluff. No performing. Just a
          quieter way back to yourself.
        </p>

        <form onSubmit={onSubmit} className="mt-7 grid gap-3 md:grid-cols-[1fr_1fr_auto]" data-testid="challenge-signup-form">
          <input
            type="text"
            required
            placeholder="Your first name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="challenge-input-name"
            className="rounded-full border px-5 py-3 text-sm outline-none focus:ring-2 transition-shadow"
            style={{ background: "#fff", borderColor: "rgba(27,67,50,0.2)", color: "#2A2621" }}
          />
          <input
            type="email"
            required
            placeholder="Your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="challenge-input-email"
            className="rounded-full border px-5 py-3 text-sm outline-none focus:ring-2 transition-shadow"
            style={{ background: "#fff", borderColor: "rgba(27,67,50,0.2)", color: "#2A2621" }}
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="challenge-submit"
            className="btn-gold disabled:opacity-70"
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : null}
            {done ? "You're in \u2713" : "Send me Day 1"}
          </button>
        </form>
        <p className="mt-4 text-xs" style={{ color: "#6E6759" }}>
          No spam &mdash; only the challenge and the occasional note. Unsubscribe any time.
        </p>
      </div>
    </div>
  );
}
